import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Chip,
  Stack,
  FormControlLabel,
  Switch,
  InputAdornment,
  IconButton,
  Menu,
  Card,
  CardContent
} from '@mui/material';
import {
  Event as EventIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useAuth } from '../../context/AuthContext';
import { eventAPI } from '../../services/api';
import AdminAccessControl from '../common/AdminAccessControl';
import { Event, EventStatus, CreateEventRequest, UpdateEventRequest } from '../../types';

interface EventFormData {
  title: string;
  description: string;
  category: string;
  eventDateTime: Date | null;
  cost: number;
  maxRegistrants: number | null;
  registrationStartDate: Date | null;
  registrationDeadline: Date | null;
  allowRegistrationUntilStart: boolean;
  waitlistEnabled: boolean;
  guestsAllowed: boolean;
  maxGuestsPerMember: number | null;
  totalMaxGuests: number | null;
  guestFee: number;
  location: string;
  notes: string;
  status: EventStatus;
}

const initialFormData: EventFormData = {
  title: '',
  description: '',
  category: '',
  eventDateTime: null,
  cost: 0,
  maxRegistrants: null,
  registrationStartDate: null,
  registrationDeadline: null,
  allowRegistrationUntilStart: false,
  waitlistEnabled: false,
  guestsAllowed: false,
  maxGuestsPerMember: null,
  totalMaxGuests: null,
  guestFee: 0,
  location: '',
  notes: '',
  status: EventStatus.DRAFT
};

const EventManagementPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [currentEventId, setCurrentEventId] = useState<string | null>(null);
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
  const [formData, setFormData] = useState<EventFormData>(initialFormData);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [dialogSuccess, setDialogSuccess] = useState<string | null>(null);

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);

  // Date change confirmation dialog
  const [dateChangeDialogOpen, setDateChangeDialogOpen] = useState<boolean>(false);
  const [pendingEventData, setPendingEventData] = useState<any>(null);

  // Cancel event confirmation dialog
  const [cancelEventDialogOpen, setCancelEventDialogOpen] = useState<boolean>(false);
  const [eventToCancel, setEventToCancel] = useState<Event | null>(null);

  // Menu state for actions
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  // Fetch events
  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await eventAPI.getAll({ includeRegistrations: true });
      setEvents(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Clear dialog messages after 5 seconds
  useEffect(() => {
    if (dialogSuccess || dialogError) {
      const timer = setTimeout(() => {
        setDialogSuccess(null);
        setDialogError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [dialogSuccess, dialogError]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle select changes
  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle switch changes
  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  // Handle date changes
  const handleDateChange = (field: string) => (date: Date | null) => {
    setFormData({
      ...formData,
      [field]: date
    });
  };

  // Open create dialog
  const handleOpenCreateDialog = () => {
    setFormData(initialFormData);
    setEditMode(false);
    setCurrentEventId(null);
    setCurrentEvent(null);
    setDialogError(null);
    setDialogSuccess(null);
    setDialogOpen(true);
  };

  // Open edit dialog
  const handleOpenEditDialog = (event: Event) => {
    setFormData({
      title: event.title,
      description: event.description,
      category: event.category || '',
      eventDateTime: new Date(event.eventDateTime),
      cost: event.cost,
      maxRegistrants: event.maxRegistrants || null,
      registrationStartDate: event.registrationStartDate ? new Date(event.registrationStartDate) : null,
      registrationDeadline: event.registrationDeadline ? new Date(event.registrationDeadline) : null,
      allowRegistrationUntilStart: event.allowRegistrationUntilStart,
      waitlistEnabled: event.waitlistEnabled,
      guestsAllowed: event.guestsAllowed || false,
      maxGuestsPerMember: event.maxGuestsPerMember || null,
      totalMaxGuests: event.totalMaxGuests || null,
      guestFee: event.guestFee || 0,
      location: event.location || '',
      notes: event.notes || '',
      status: event.status
    });
    setEditMode(true);
    setCurrentEventId(event.id);
    setCurrentEvent(event);
    setDialogError(null);
    setDialogSuccess(null);
    setDialogOpen(true);
  };

  // Close dialog
  const closeDialog = () => {
    setDialogOpen(false);
    setCurrentEventId(null);
    setCurrentEvent(null);
    setFormData(initialFormData);
    setDialogError(null);
    setDialogSuccess(null);
  };

  // Submit form
  const handleSubmit = async () => {
    if (!formData.title || !formData.description || !formData.eventDateTime) {
      setDialogError('Please fill in all required fields');
      return;
    }

    // Validate registration settings
    if (!formData.registrationDeadline && !formData.allowRegistrationUntilStart) {
      setDialogError('Registration deadline required: Please either set a registration deadline date or enable "Allow registration until event starts" option below.');
      return;
    }

    // Validate waitlist settings - don't allow disabling waitlist if there are users on it
    if (editMode && currentEvent && currentEvent.waitlistEnabled && !formData.waitlistEnabled && (currentEvent.waitlistCount || 0) > 0) {
      setDialogError(`Cannot disable waitlist: There are ${currentEvent.waitlistCount} users currently on the waitlist. Please manage these registrations first.`);
      return;
    }

    // Clear any previous dialog messages
    setDialogError(null);
    setDialogSuccess(null);
    
    const eventData = {
      title: formData.title,
      description: formData.description,
      category: formData.category || undefined,
      eventDateTime: formData.eventDateTime!.toISOString(),
      cost: formData.cost,
      maxRegistrants: formData.maxRegistrants || undefined,
      registrationStartDate: formData.registrationStartDate?.toISOString(),
      registrationDeadline: formData.registrationDeadline?.toISOString(),
      allowRegistrationUntilStart: formData.allowRegistrationUntilStart,
      waitlistEnabled: formData.waitlistEnabled,
      guestsAllowed: formData.guestsAllowed,
      maxGuestsPerMember: formData.maxGuestsPerMember || undefined,
      totalMaxGuests: formData.totalMaxGuests || undefined,
      guestFee: formData.guestFee,
      location: formData.location || undefined,
      notes: formData.notes || undefined,
      status: formData.status
    };

    // Check if we're editing an event and the date has changed and there are registered users
    if (editMode && currentEvent && (currentEvent.registeredCount || 0) > 0) {
      const originalDate = new Date(currentEvent.eventDateTime);
      const newDate = formData.eventDateTime;
      
      // Check if date/time has changed (compare timestamps)
      if (originalDate.getTime() !== newDate.getTime()) {
        // Show confirmation dialog for date change
        setPendingEventData(eventData);
        setDateChangeDialogOpen(true);
        return;
      }
    }

    // Proceed with normal submit if no date change confirmation needed
    await performEventUpdate(eventData);
  };

  // Perform the actual event update
  const performEventUpdate = async (eventData: any) => {
    setSubmitting(true);
    try {
      if (editMode && currentEventId) {
        await eventAPI.update(currentEventId, eventData);
        setSuccess('Event updated successfully');
      } else {
        await eventAPI.create(eventData);
        setSuccess('Event created successfully');
      }

      closeDialog();
      await fetchEvents();
    } catch (error: any) {
      setDialogError(error.response?.data?.message || 'Failed to save event');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle date change confirmation
  const handleConfirmDateChange = async () => {
    setDateChangeDialogOpen(false);
    if (pendingEventData) {
      await performEventUpdate(pendingEventData);
      setPendingEventData(null);
    }
  };

  // Handle date change cancellation
  const handleCancelDateChange = () => {
    setDateChangeDialogOpen(false);
    setPendingEventData(null);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!eventToDelete) return;

    setSubmitting(true);
    try {
      await eventAPI.delete(eventToDelete.id);
      setSuccess('Event deleted successfully');
      setDeleteDialogOpen(false);
      setEventToDelete(null);
      await fetchEvents();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to delete event');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle cancel event
  const handleCancelEvent = async () => {
    if (!eventToDelete) return;

    // Check if there are registered users
    if ((eventToDelete.registeredCount || 0) > 0) {
      // Show confirmation dialog for cancellation with registered users
      setEventToCancel(eventToDelete);
      setCancelEventDialogOpen(true);
      setDeleteDialogOpen(false);
      return;
    }

    // Proceed with normal cancellation if no registered users
    await performEventCancellation(eventToDelete);
  };

  // Perform the actual event cancellation
  const performEventCancellation = async (event: Event) => {
    setSubmitting(true);
    try {
      await eventAPI.update(event.id, { status: EventStatus.CANCELLED });
      setSuccess('Event cancelled successfully');
      setDeleteDialogOpen(false);
      setEventToDelete(null);
      await fetchEvents();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to cancel event');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle cancel event with registered users confirmation
  const handleConfirmCancelEvent = async () => {
    if (!eventToCancel) return;

    setCancelEventDialogOpen(false);
    setSubmitting(true);
    
    try {
      // First, get all registrations for this event
      const registrationsResponse = await eventAPI.getEventRegistrations(eventToCancel.id);
      const allRegistrations = [
        ...registrationsResponse.data.registered,
        ...registrationsResponse.data.waitlisted
      ];

      // Cancel all registrations
      const cancelPromises = allRegistrations.map(registration => 
        eventAPI.cancelRegistration(
          registration.id,
          `Event cancelled by admin: ${user?.email}`
        )
      );

      await Promise.all(cancelPromises);

      // Then cancel the event
      await eventAPI.update(eventToCancel.id, { status: EventStatus.CANCELLED });
      
      setSuccess(`Event cancelled successfully. ${allRegistrations.length} participants have been unregistered.`);
      setEventToCancel(null);
      await fetchEvents();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to cancel event and unregister participants');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle cancel event cancellation
  const handleCancelEventCancellation = () => {
    setCancelEventDialogOpen(false);
    setEventToCancel(null);
  };

  // Handle menu actions
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, eventItem: Event) => {
    setAnchorEl(event.currentTarget);
    setSelectedEvent(eventItem);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEvent(null);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Get status color
  const getStatusColor = (status: EventStatus) => {
    switch (status) {
      case EventStatus.PUBLISHED:
        return 'success';
      case EventStatus.DRAFT:
        return 'default';
      case EventStatus.CANCELLED:
        return 'error';
      case EventStatus.COMPLETED:
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <RouterLink to="/dashboard" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Dashboard
          </RouterLink>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <EventIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Event Management
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Event Management
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Create, manage, and track events for your club
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              New Event
            </Button>
          </Box>
        </Paper>

        {/* Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Events Table */}
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Event</TableCell>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell>Registrations</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <EventIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No events found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Create your first event to get started
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  events.map((event) => (
                    <TableRow key={event.id} hover>
                      <TableCell>
                        <Box>
                          <Typography 
                            variant="subtitle2" 
                            fontWeight="bold"
                            component={RouterLink}
                            to={`/events/${event.id}`}
                            sx={{ 
                              textDecoration: 'none',
                              color: 'primary.main',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {event.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {event.description.length > 60 
                              ? `${event.description.substring(0, 60)}...`
                              : event.description
                            }
                          </Typography>
                          {event.category && (
                            <Chip label={event.category} size="small" sx={{ mt: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(event.eventDateTime)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {event.location || 'Not specified'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {event.cost > 0 ? formatCurrency(event.cost) : 'Free'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {event.registeredCount || 0}
                            {event.maxRegistrants && ` / ${event.maxRegistrants}`}
                          </Typography>
                          {event.waitlistCount && event.waitlistCount > 0 && (
                            <Typography variant="caption" color="warning.main">
                              {event.waitlistCount} waitlisted
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={event.status.replace('_', ' ').toUpperCase()}
                          color={getStatusColor(event.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={(e) => handleMenuClick(e, event)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {selectedEvent && selectedEvent.status !== EventStatus.CANCELLED && (
            <MenuItem onClick={() => {
              if (selectedEvent) handleOpenEditDialog(selectedEvent);
              handleMenuClose();
            }}>
              <EditIcon sx={{ mr: 1 }} />
              Edit Event
            </MenuItem>
          )}
          {selectedEvent && selectedEvent.status !== EventStatus.CANCELLED && (
            selectedEvent && (selectedEvent.registeredCount || 0) === 0 ? (
              <MenuItem 
                onClick={() => {
                  setEventToDelete(selectedEvent);
                  setDeleteDialogOpen(true);
                  handleMenuClose();
                }}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon sx={{ mr: 1 }} />
                Delete Event
              </MenuItem>
            ) : (
              <MenuItem 
                onClick={() => {
                  setEventToDelete(selectedEvent);
                  setDeleteDialogOpen(true);
                  handleMenuClose();
                }}
                sx={{ color: 'warning.main' }}
              >
                <CancelIcon sx={{ mr: 1 }} />
                Cancel Event
              </MenuItem>
            )
          )}
        </Menu>

        {/* Create/Edit Event Dialog */}
        <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editMode ? 'Edit Event' : 'Create New Event'}
          </DialogTitle>
          <DialogContent>
            {/* Error and Success Messages */}
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
                {dialogError}
              </Alert>
            )}
            {dialogSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDialogSuccess(null)}>
                {dialogSuccess}
              </Alert>
            )}
            
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Title */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  name="title"
                  label="Event Title"
                  fullWidth
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Description */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  name="description"
                  label="Description"
                  fullWidth
                  required
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Category and Location */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  name="category"
                  label="Category"
                  fullWidth
                  value={formData.category}
                  onChange={handleInputChange}
                  placeholder="e.g., Social, Fitness, Educational"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  name="location"
                  label="Location"
                  fullWidth
                  value={formData.location}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Event Date & Time */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <DateTimePicker
                  label="Event Date & Time"
                  value={formData.eventDateTime}
                  onChange={handleDateChange('eventDateTime')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true
                    }
                  }}
                />
              </Grid>

              {/* Cost */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  name="cost"
                  label="Cost"
                  type="number"
                  fullWidth
                  value={formData.cost}
                  onChange={handleInputChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              {/* Max Registrants */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  name="maxRegistrants"
                  label="Max Registrants"
                  type="number"
                  fullWidth
                  value={formData.maxRegistrants || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    maxRegistrants: e.target.value ? parseInt(e.target.value) : null
                  })}
                  placeholder="Leave empty for unlimited"
                  inputProps={{ min: 1 }}
                />
              </Grid>

              {/* Status */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleSelectChange}
                    label="Status"
                  >
                    <MenuItem value={EventStatus.DRAFT}>Draft</MenuItem>
                    <MenuItem value={EventStatus.PUBLISHED}>Published</MenuItem>
                    <MenuItem value={EventStatus.CANCELLED}>Cancelled</MenuItem>
                    <MenuItem value={EventStatus.COMPLETED}>Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Registration Settings */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Registration Settings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  You must either set a registration deadline OR enable "Allow registration until event starts"
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DateTimePicker
                      label="Registration Start Date"
                      value={formData.registrationStartDate}
                      onChange={handleDateChange('registrationStartDate')}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          helperText: "Leave empty to allow immediate registration"
                        }
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DateTimePicker
                      label="Registration Deadline"
                      value={formData.registrationDeadline}
                      onChange={handleDateChange('registrationDeadline')}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          helperText: !formData.registrationDeadline && !formData.allowRegistrationUntilStart 
                            ? "Required if 'Allow registration until start' is disabled"
                            : "Leave empty to use event start time"
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="allowRegistrationUntilStart"
                        checked={formData.allowRegistrationUntilStart}
                        onChange={handleSwitchChange}
                      />
                    }
                    label="Allow registration until event starts"
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        name="waitlistEnabled"
                        checked={formData.waitlistEnabled}
                        onChange={handleSwitchChange}
                        disabled={!!(editMode && currentEvent && formData.waitlistEnabled && (currentEvent.waitlistCount || 0) > 0)}
                      />
                    }
                    label={
                      editMode && currentEvent && formData.waitlistEnabled && (currentEvent.waitlistCount || 0) > 0
                        ? `Enable waitlist when full (${currentEvent.waitlistCount} users currently waitlisted)`
                        : "Enable waitlist when full"
                    }
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        color: editMode && currentEvent && formData.waitlistEnabled && (currentEvent.waitlistCount || 0) > 0 
                          ? 'text.secondary' 
                          : 'inherit'
                      }
                    }}
                  />
                </Stack>

                {/* Warning when neither deadline nor allow-until-start is set */}
                {!formData.registrationDeadline && !formData.allowRegistrationUntilStart && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Please set a registration deadline above OR enable "Allow registration until event starts" to continue.
                  </Alert>
                )}

                {/* Info when waitlist cannot be disabled due to existing waitlisted users */}
                {editMode && currentEvent && formData.waitlistEnabled && (currentEvent.waitlistCount || 0) > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Waitlist cannot be disabled while {currentEvent.waitlistCount} users are waitlisted. 
                    Manage these registrations first or convert them to full registrations.
                  </Alert>
                )}
              </Grid>

              {/* Guest Settings */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Guest Settings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure whether members can bring guests to this event
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      name="guestsAllowed"
                      checked={formData.guestsAllowed}
                      onChange={handleSwitchChange}
                    />
                  }
                  label="Allow members to bring guests"
                  sx={{ mb: 2 }}
                />

                {formData.guestsAllowed && (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        name="maxGuestsPerMember"
                        label="Max Guests Per Member"
                        type="number"
                        fullWidth
                        value={formData.maxGuestsPerMember || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          maxGuestsPerMember: e.target.value ? parseInt(e.target.value) : null
                        })}
                        helperText="Leave empty for unlimited"
                        inputProps={{ min: 1 }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        name="totalMaxGuests"
                        label="Total Max Guests"
                        type="number"
                        fullWidth
                        value={formData.totalMaxGuests || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          totalMaxGuests: e.target.value ? parseInt(e.target.value) : null
                        })}
                        helperText="Max guests across all attendees"
                        inputProps={{ min: 1 }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        name="guestFee"
                        label="Guest Fee"
                        type="number"
                        fullWidth
                        value={formData.guestFee}
                        onChange={handleInputChange}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                        helperText="Fee charged per guest"
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>

              {/* Notes */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  name="notes"
                  label="Additional Notes"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Any additional information or instructions..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={20} /> : (editMode ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete/Cancel Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>
            {eventToDelete && (eventToDelete.registeredCount || 0) > 0 ? 'Cancel Event' : 'Delete Event'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {eventToDelete && (eventToDelete.registeredCount || 0) > 0 ? (
                <>
                  Are you sure you want to cancel "{eventToDelete?.title}"? 
                  
                  <Alert severity="info" sx={{ mt: 2 }}>
                    This event has {eventToDelete.registeredCount} registered participants. 
                    They will be notified of the cancellation. <br /><br />
                    <b>This is an irreversible action.</b>
                  </Alert>
                </>
              ) : (
                <>
                  Are you sure you want to delete "{eventToDelete?.title}"? 
                  This action cannot be undone and will permanently remove the event.
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={eventToDelete && (eventToDelete.registeredCount || 0) > 0 ? handleCancelEvent : handleDelete}
              color={eventToDelete && (eventToDelete.registeredCount || 0) > 0 ? "warning" : "error"}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={20} /> : 
                eventToDelete && (eventToDelete.registeredCount || 0) > 0 ? 'Cancel Event' : 'Delete'
              }
            </Button>
          </DialogActions>
        </Dialog>

        {/* Date Change Confirmation Dialog */}
        <Dialog open={dateChangeDialogOpen} onClose={handleCancelDateChange}>
          <DialogTitle>Reschedule Event</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to reschedule the event? All the users who have registered for the event will be notified of the change.
            </DialogContentText>
            {currentEvent && (currentEvent.registeredCount || 0) > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This event has {currentEvent.registeredCount} registered participants who will be notified of the date change.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDateChange}>Cancel</Button>
            <Button 
              onClick={handleConfirmDateChange}
              color="warning"
              disabled={submitting}
              variant="contained"
            >
              {submitting ? <CircularProgress size={20} /> : 'Yes, Reschedule Event'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Event with Registrations Confirmation Dialog */}
        <Dialog open={cancelEventDialogOpen} onClose={handleCancelEventCancellation}>
          <DialogTitle>Cancel Event</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to cancel this event? All users who have registered for the event will be unregistered and notified of the cancellation. This is an irreversible action.
            </DialogContentText>
            {eventToCancel && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This event has {(eventToCancel.registeredCount || 0) + (eventToCancel.waitlistCount || 0)} participants 
                ({eventToCancel.registeredCount || 0} registered, {eventToCancel.waitlistCount || 0} waitlisted) 
                who will be automatically unregistered.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelEventCancellation}>Cancel</Button>
            <Button 
              onClick={handleConfirmCancelEvent}
              color="error"
              disabled={submitting}
              variant="contained"
            >
              {submitting ? <CircularProgress size={20} /> : 'Yes, Cancel Event'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default EventManagementPage;
